import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Link } from 'react-router-dom';
import {
    Search,
    CheckCircle,
    HelpCircle,
    User,
    Building2,
    ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_review: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
};

const PRIORITY_COLORS = {
    low: 'text-gray-500',
    medium: 'text-blue-500',
    high: 'text-orange-500',
    urgent: 'text-red-500 font-bold',
};

export default function AdminTickets() {
    const { getAuthHeader } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        fetchTickets();
    }, [statusFilter]);

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/admin/tickets`, {
                headers: getAuthHeader(),
                params: { status: statusFilter === 'all' ? undefined : statusFilter }
            });
            setTickets(response.data.tickets);
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
            toast.error('Failed to load tickets');
        } finally {
            setLoading(false);
        }
    };

    const filteredTickets = tickets.filter(t =>
        t.subject.toLowerCase().includes(search.toLowerCase()) ||
        t.id.toLowerCase().includes(search.toLowerCase()) ||
        t.user_name.toLowerCase().includes(search.toLowerCase())
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="p-6 lg:p-8 space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-navy-900">Support Tickets</h1>
                    <p className="text-navy-600">Manage customer and agency support requests</p>
                </div>

                <div className="flex gap-2 bg-white p-1 rounded-lg border border-navy-200 overflow-x-auto">
                    {['all', 'pending', 'in_review', 'resolved', 'closed'].map((status) => (
                        <button
                            key={status}
                            onClick={() => setStatusFilter(status)}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${statusFilter === status
                                    ? 'bg-navy-900 text-white'
                                    : 'text-navy-600 hover:bg-navy-50'
                                }`}
                        >
                            {status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-white rounded-xl border border-navy-100 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-navy-100 bg-navy-50/50 flex gap-4">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                        <Input
                            placeholder="Search by ID, subject, or user..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-9 bg-white"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-navy-500">Loading tickets...</div>
                ) : filteredTickets.length === 0 ? (
                    <div className="p-12 text-center">
                        <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                        <h3 className="text-lg font-medium text-navy-900">All caught up!</h3>
                        <p className="text-navy-500">No tickets found with status "{statusFilter}"</p>
                    </div>
                ) : (
                    <div className="divide-y divide-navy-100">
                        {filteredTickets.map((ticket) => (
                            <Link
                                key={ticket.id}
                                to={`${ticket.id}`} // Relative path to ./tickets/:id
                                className="block p-4 hover:bg-navy-50 transition-colors"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-1">
                                            <span className="font-semibold text-navy-900">#{ticket.id.slice(0, 8)}</span>
                                            <Badge variant="secondary" className={STATUS_COLORS[ticket.status]}>
                                                {ticket.status.replace('_', ' ').toUpperCase()}
                                            </Badge>
                                            <Badge variant="outline" className="capitalize">
                                                {ticket.category}
                                            </Badge>
                                            {ticket.priority === 'urgent' && (
                                                <Badge variant="destructive" className="flex items-center gap-1">
                                                    <ShieldAlert className="w-3 h-3" /> Urgent
                                                </Badge>
                                            )}
                                        </div>
                                        <h3 className="font-medium text-navy-800 mb-1">{ticket.subject}</h3>
                                        <div className="flex items-center gap-4 text-sm text-navy-500">
                                            <span className="flex items-center gap-1">
                                                {ticket.user_type === 'agency' ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />}
                                                {ticket.user_name}
                                            </span>
                                            <span>•</span>
                                            <span>{formatDate(ticket.created_at)}</span>
                                        </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <span className={`text-xs font-medium uppercase ${PRIORITY_COLORS[ticket.priority]}`}>
                                            {ticket.priority} Priority
                                        </span>
                                        <span className="text-xs text-navy-400 block mt-1">
                                            Last updated: {formatDate(ticket.updated_at || ticket.created_at)}
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
